import { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'src/components/ui/button';
import { Button as PrimeButton } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useUploadImageMutation } from '../../../redux/survey/survey.endpoints';
import { useUpdateClientMutation } from '../../../redux/organization/organization.endpoints';
import { Client } from '../../../redux/organization/organization.types';
import { ReactComponent as ImageIcon } from '../../../assets/icons/image.svg';
import { ALLOWED_IMAGE_TYPES, ALLOWED_IMAGE_TYPE_STRING } from '../../image-upload/image-uploads.constants';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../redux/redux-store';
import { showNotification } from '../../../redux/notification/notification.slice';

interface EditBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
}

export const EditBrandModal = ({ isOpen, onClose, client }: EditBrandModalProps) => {
  const userState = useSelector((state: RootState) => state.userState);
  const dispatch = useDispatch();
  const isOrgAdmin = userState.permissions?.groups?.includes('org_admin');
  const [uploadImage] = useUploadImageMutation();
  const [updateClient] = useUpdateClientMutation();
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);
  const [brandTitle, setBrandTitle] = useState(client?.title || '');
  const [isTitlePending, setIsTitlePending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (client?.brand) {
      setLocalImageUrl(client.brand);
    }
    if (client?.title) {
      setBrandTitle(client.title);
    }
  }, [client?.brand, client?.title]);

  useEffect(() => {
    const handleUpload = async() => {
      if (uploadedImage && client) {
        if (uploadedImage.size > 500 * 1024) { // 500KB in bytes
          dispatch(showNotification({
            summary: 'File size exceeds 500KB limit',
            theme: 'warning',
            sticky: true,
          }));
          setUploadedImage(null);
          setLocalImageUrl(null);
          return;
        }

        try {
          const formData = new FormData();
          formData.append('file', uploadedImage);
          formData.append('type', 'client');
          formData.append('uuid', client.ext_client_id);
          const response = await uploadImage({ formData, uuid: client.ext_client_id }).unwrap();
          setLocalImageUrl(response.item.url);
          await updateClient({
            clientId: client.ext_client_id,
            data: {
              ...client,
              brand: response.item.url,
            },
          });
          setUploadedImage(null);
        } catch (error) {
          console.error('Error uploading image:', error);
          dispatch(showNotification({
            summary: 'Error uploading image. Please try again.',
            theme: 'warning',
            sticky: true,
          }));
        }
      }
    };

    handleUpload();
  }, [uploadedImage, uploadImage, updateClient, client, dispatch]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBrandTitle(e.target.value);
    setIsTitlePending(true);
  };

  const handleTitleSave = async() => {
    if (isTitlePending && client) {
      try {
        await updateClient({
          clientId: client.ext_client_id,
          data: {
            ...client,
            title: brandTitle,
          },
        });
        setIsTitlePending(false);
      } catch (error) {
        console.error('Error updating brand title:', error);
      }
    }
  };

  const handleImageUpload = (file: File) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLocalImageUrl(reader.result as string);
        setUploadedImage(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && ALLOWED_IMAGE_TYPES.includes(file.type)) {
      handleImageUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && ALLOWED_IMAGE_TYPES.includes(file.type)) {
      handleImageUpload(file);
    }
  };

  const renderLogoSection = () => {
    if (localImageUrl) {
      return (
        <div className="flex flex-col items-center gap-4 mt-6">
          <img
            src={localImageUrl}
            alt="Client logo"
            className="max-w-[200px] max-h-[200px] object-contain"
          />
          <div className="flex gap-2">
            <PrimeButton
              label="Change Logo"
              onClick={() => setLocalImageUrl(null)}
              className="p-button-secondary"
            />
          </div>
        </div>
      );
    }

    return (
      <div
        className="relative flex h-32 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-white shadow-md"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center gap-2">
          <i className="pi pi-upload text-2xl text-gray-401" />
          <span className="ml-2 flex items-center space-x-2 text-gray-401">
            <ImageIcon className='size-4 object-contain fill-black opacity-50' />
            <div className="flex">
              <span className="mr-1 underline">
                Upload logo
              </span>
              {' '}
              or drag and drop here
            </div>
          </span>
        </div>
        <div className='absolute bottom-3 left-0 right-0 text-center text-gray-401 text-xs hidden sm:block'>
          Please upload an image under 500 Kb. Allowed file types are png, jpg, and jpeg.
        </div>
        <input
          ref={fileInputRef}
          id="logo-upload"
          type="file"
          accept={ALLOWED_IMAGE_TYPE_STRING}
          className="absolute inset-0 hidden"
          onChange={handleFileInput}
        />
      </div>
    );
  };

  return (
    <Dialog
      visible={isOpen}
      onHide={onClose}
      header="Edit"
      style={{ width: '90vw', maxWidth: '500px' }}
      contentStyle={{ paddingLeft: 16, paddingRight: 16 }}
    >
      <div className="flex flex-col gap-6 pt-4 w-full">
        {isOrgAdmin && (
          <div className="flex flex-col gap-2 w-full">
            <label
              htmlFor="brand-title"
              className="text-sm font-medium"
            >
              Name
            </label>
            <div className="flex gap-2 w-full flex-col sm:flex-row">
              <InputText
                id="brand-title"
                value={brandTitle}
                onChange={handleTitleChange}
                className="flex-1 p-inputtext-lg w-full"
                style={{
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '1.1rem',
                  backgroundColor: '#f8f9fa',
                }}
              />
              <Button
                onClick={handleTitleSave}
                disabled={!isTitlePending}
                variant="primary"
                size="lg"
                className="w-full sm:w-auto"
              >
                Save
              </Button>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-2 w-full">
          <label className="text-sm font-medium">
            Logo
          </label>
          {renderLogoSection()}
        </div>
      </div>
    </Dialog>
  );
};